import {
  WSFilters,
  WSInfiniteScroll,
  WSList,
  WSTableColumnSortDirection
} from "@wingspanhq/fe-component-library";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryWorkLogs } from "../../../../query/workLog/queries/useQueryWorkLogs";
import { useQueryWorkLogsSummary } from "../../../../query/workLog/queries/useQueryWorkLogsSummary";
import { WSQueries } from "../../../../query/WSQuery";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { useInvoicesMap } from "../../hooks/useInvoicesMap";
import { usePayersMap } from "../../hooks/usePayersMap";
import { getPathWorkLogPayeeDetails } from "../../paths";
import {
  WorkLogsFilterEmptyState,
  WorkLogsPayeeEmptyState
} from "../WorkLogsEmptyStates";
import { useBuildConfig } from "./filters/config";
import { createListRequest } from "./filters/request";
import {
  Filter,
  SortField,
  WorkLogPayeeTabFilterStatus
} from "./filters/types";
import { TableWorkLogsPayeeView } from "./TableWorkLogsPayeeView";

type Props = {
  status?: Filter["status"];
};

export const FilterableTableWorkLogsPayeeView: React.FC<Props> = props => {
  const userId = useUserId();
  const history = useHistory();

  const { urlQuery, setUrlQuery } = useUrlQuery<Filter, SortField>();

  const request = useMemo(
    () =>
      createListRequest(
        userId,
        {
          status: props.status,
          ...urlQuery.filter
        },
        urlQuery.sort
      ),
    [userId, props.status, urlQuery.filter, urlQuery.sort]
  );

  const filtersConfig = useBuildConfig();

  const queryWorkLogs = useQueryWorkLogs(request);
  const queryWorkLogsSummary = useQueryWorkLogsSummary(request);

  const payersMap = usePayersMap(queryWorkLogs.data);
  const { invoicesMap } = useInvoicesMap(queryWorkLogs.data);

  const hasFilters = useMemo(
    () => Object.keys(urlQuery.filter || {}).length > 0,
    [urlQuery.filter]
  );

  const sortByUpdatedAt = useCallback(
    (newDirection: WSTableColumnSortDirection) => {
      setUrlQuery(prev => ({
        ...prev,
        sort: {
          field: "updatedAt",
          direction: newDirection
        }
      }));
    },
    [setUrlQuery]
  );

  return (
    <WSList gap="2XL">
      <WSFilters<Filter>
        values={urlQuery.filter}
        onFilter={newValue => {
          setUrlQuery({ filter: newValue });
        }}
        primaryFilters={filtersConfig}
        info={{
          count: queryWorkLogsSummary.data?.listSize || 0,
          amount: queryWorkLogsSummary.data?.totalAmount
        }}
      />

      <WSQueries queries={{ queryWorkLogs }}>
        {({ queryWorkLogsData }) =>
          hasFilters && queryWorkLogsData.length === 0 ? (
            <WorkLogsFilterEmptyState />
          ) : queryWorkLogsData.length === 0 ? (
            <WorkLogsPayeeEmptyState
              status={props.status as WorkLogPayeeTabFilterStatus}
            />
          ) : (
            <WSInfiniteScroll
              mt="M"
              onLoad={() => queryWorkLogs.fetchNextPage()}
              loadMore={queryWorkLogsData.length > 0}
              endOfList={!queryWorkLogs.hasNextPage}
              loading={!!queryWorkLogs.isFetchingNextPage}
            >
              <TableWorkLogsPayeeView
                workLogs={queryWorkLogsData}
                payersMap={payersMap}
                invoicesMap={invoicesMap}
                onRowClick={workLog => {
                  history.push(getPathWorkLogPayeeDetails(workLog.workLogId));
                }}
                onSortByUpdatedAt={sortByUpdatedAt}
                updatedAtDirection={urlQuery.sort?.direction}
              />
            </WSInfiniteScroll>
          )
        }
      </WSQueries>
    </WSList>
  );
};
